import { useState } from 'react'
import type { CSSProperties, ElementType, ComponentPropsWithoutRef } from 'react'

type HovProps<T extends ElementType> = {
  as?: T
  style?: CSSProperties
  /** Merged over `style` while the pointer is over the element. */
  hoverStyle?: CSSProperties
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'style'>

/**
 * Element with a hover style, standing in for the design doc's `style-hover`
 * attribute. Kept as inline state rather than a CSS class so the generated
 * screens need no stylesheet of their own.
 */
export function Hov<T extends ElementType = 'div'>({
  as,
  style,
  hoverStyle,
  ...rest
}: HovProps<T>) {
  const [hovered, setHovered] = useState(false)
  const Tag = (as ?? 'div') as ElementType

  const base = hoverStyle ? expandConflicts(style, hoverStyle) : style

  return (
    <Tag
      {...rest}
      style={hovered && hoverStyle ? { ...base, ...hoverStyle } : base}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    />
  )
}

/** Longhands each shorthand would silently fight with across a re-render. */
const SHORTHANDS: Record<string, readonly string[]> = {
  border: ['borderWidth', 'borderStyle', 'borderColor'],
  background: ['backgroundColor', 'backgroundImage'],
  font: ['fontStyle', 'fontWeight', 'fontSize', 'lineHeight', 'fontFamily'],
}

/**
 * The design writes hover states as longhands (`border-color`) over a base
 * shorthand (`border`). React warns — correctly — when a longhand appears and
 * disappears alongside a shorthand between renders, because the result depends
 * on property order. Expanding the shorthand up front keeps the key set stable
 * in both states, so the design's intent survives without the warning.
 */
function expandConflicts(
  style: CSSProperties | undefined,
  hoverStyle: CSSProperties
): CSSProperties | undefined {
  if (!style) return style

  let out: Record<string, unknown> | null = null

  for (const [shorthand, longhands] of Object.entries(SHORTHANDS)) {
    const value = (style as Record<string, unknown>)[shorthand]
    if (value === undefined) continue
    const clashing = longhands.filter((l) => l in hoverStyle)
    if (!clashing.length) continue

    out ??= { ...style }
    // Re-declaring the shorthand first, then the longhands the hover state
    // touches, makes both renders agree on which keys exist.
    for (const longhand of clashing) {
      out[longhand] = out[longhand] ?? inferLonghand(shorthand, longhand, String(value))
    }
  }

  return (out ?? style) as CSSProperties
}

/** `border: 1px solid X` -> borderColor `X`. Falls back to the shorthand. */
function inferLonghand(shorthand: string, longhand: string, value: string): string {
  if (shorthand !== 'border') return value
  const parts = value.trim().split(/\s+(?![^(]*\))/)
  if (longhand === 'borderWidth') return parts[0] ?? value
  if (longhand === 'borderStyle') return parts[1] ?? 'solid'
  return parts.slice(2).join(' ') || value
}
